/**
 * Café-Luise im Luisenhof · Fortschrittliche Verbesserung
 *
 * Die Seite ist ohne dieses Skript vollständig lesbar und bedienbar. Hier stehen
 * nur vier Dinge, mehr gehört nicht hinein:
 *
 *   1. Mobiles Menü öffnen und schließen, mit Escape, Fokusfalle und aria-expanded
 *   2. Der angeklickte Weg im Seitenkopf bleibt markiert, bis die neue Seite malt
 *   3. Zusätzliche Prüfung der Formulare im Browser. Sie ergänzt die Prüfung auf
 *      dem Server und ersetzt sie niemals.
 *   4. Bildfolgen: Die Bilder einer Stelle loesen einander ab, und der Gast
 *      kann wischen. Bei reduzierter Bewegung entfaellt nur die Blende.
 *
 * Kein Framework, kein Router, keine Zustandsverwaltung, keine Abhängigkeit zur
 * Laufzeit. Es wird kein fremder Host angesprochen; nachgeladen wird einzig der
 * Inhalt eines Textbausteins für das Bearbeiten-Fenster der Angemeldeten, und
 * das vom eigenen Server.
 */

const wurzel: HTMLElement = document.documentElement;

/**
 * Ab dieser Breite entfaellt der Menue-Knopf.
 *
 * Dieselbe Zahl steht in site.css in den @media-Regeln, die .burger ausblenden
 * und .hauptnav einblenden. Hier stand einmal 1070: Zwischen 900 und 1070
 * Punkten war der Knopf laengst weg, und die geoeffnete Tafel schloss sich beim
 * Aufziehen des Fensters trotzdem nicht -- sie blieb ueber der Menueleiste
 * stehen, bis jemand ihren eigenen Schliessknopf fand.
 */
const BREITE_DESKTOP = '(min-width: 900px)';

const FOKUSSIERBAR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

// ---------------------------------------------------------------------------
// Kleine Helfer
// ---------------------------------------------------------------------------

function eins<E extends Element>(auswahl: string, bereich: ParentNode = document): E | null {
  return bereich.querySelector<E>(auswahl);
}

function alle<E extends Element>(auswahl: string, bereich: ParentNode = document): E[] {
  return Array.from(bereich.querySelectorAll<E>(auswahl));
}

function sichtbar(element: Element): boolean {
  return element.getClientRects().length > 0;
}

function auswahlZeichen(wert: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(wert);
  return wert.replace(/["\\]/g, '\\$&');
}

function zahlOder(wert: string | null): number | null {
  if (wert === null || wert.trim() === '') return null;
  const n = Number.parseInt(wert, 10);
  return Number.isFinite(n) ? n : null;
}

// ---------------------------------------------------------------------------
// 1. Mobiles Menü
// ---------------------------------------------------------------------------

function menueEinrichten(): void {
  const gefundenerSchalter = eins<HTMLElement>('#burger');
  const gefundeneTafel = eins<HTMLElement>('#overlay');
  if (gefundenerSchalter === null || gefundeneTafel === null) return;

  const schalter: HTMLElement = gefundenerSchalter;
  const tafel: HTMLElement = gefundeneTafel;
  const schliesser = alle<HTMLElement>('#overlayclose', tafel);

  let offen = false;
  let ueberlaufWurzel = '';
  let ueberlaufKoerper = '';

  if (tafel.id !== '' && !schalter.hasAttribute('aria-controls')) {
    schalter.setAttribute('aria-controls', tafel.id);
  }
  schalter.setAttribute('aria-expanded', 'false');

  function fokusZiele(): HTMLElement[] {
    return alle<HTMLElement>(FOKUSSIERBAR, tafel).filter(sichtbar);
  }

  function oeffnen(): void {
    if (offen) return;
    offen = true;

    tafel.classList.add('open');
    schalter.setAttribute('aria-expanded', 'true');

    ueberlaufWurzel = wurzel.style.overflow;
    ueberlaufKoerper = document.body.style.overflow;
    wurzel.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    const ziele = fokusZiele();
    (ziele[0] ?? tafel).focus({ preventScroll: true });
  }

  function schliessen(fokusZurueck = true): void {
    if (!offen) return;
    offen = false;

    tafel.classList.remove('open');
    schalter.setAttribute('aria-expanded', 'false');

    wurzel.style.overflow = ueberlaufWurzel;
    document.body.style.overflow = ueberlaufKoerper;

    // Falls die Seite ohne Skript über ":target" auskommt, darf die Adresse
    // nach dem Schließen nicht auf dem Menü stehen bleiben.
    const id = tafel.id;
    if (id !== '' && window.location.hash === '#' + id) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    // Der Fokus kehrt immer zum Menü-Knopf zurück, denn nur er öffnet das Menü.
    if (fokusZurueck) schalter.focus({ preventScroll: true });
  }

  schalter.addEventListener('click', (ereignis: MouseEvent) => {
    ereignis.preventDefault();
    if (offen) schliessen();
    else oeffnen();
  });

  for (const knopf of schliesser) {
    knopf.addEventListener('click', (ereignis: MouseEvent) => {
      ereignis.preventDefault();
      schliessen();
    });
  }

  // Ein Verweis im Menü führt auf eine echte Seite. Das Menü wird trotzdem
  // geschlossen, damit ein Sprungziel auf derselben Seite sichtbar wird.
  tafel.addEventListener('click', (ereignis: MouseEvent) => {
    const ziel = ereignis.target;
    if (ziel instanceof Element && ziel.closest('a[href]') !== null) schliessen(false);
  });

  document.addEventListener('keydown', (ereignis: KeyboardEvent) => {
    if (!offen) return;

    if (ereignis.key === 'Escape') {
      ereignis.preventDefault();
      schliessen();
      return;
    }

    if (ereignis.key !== 'Tab') return;

    const ziele = fokusZiele();
    if (ziele.length === 0) {
      ereignis.preventDefault();
      return;
    }

    const erstes = ziele[0] as HTMLElement;
    const letztes = ziele[ziele.length - 1] as HTMLElement;
    const aktiv = document.activeElement;

    if (ereignis.shiftKey && (aktiv === erstes || !tafel.contains(aktiv))) {
      ereignis.preventDefault();
      letztes.focus();
    } else if (!ereignis.shiftKey && (aktiv === letztes || !tafel.contains(aktiv))) {
      ereignis.preventDefault();
      erstes.focus();
    }
  });

  // Zweite Sicherung der Fokusfalle, falls der Fokus anders nach außen gerät.
  document.addEventListener('focusin', (ereignis: FocusEvent) => {
    if (!offen) return;
    const ziel = ereignis.target;
    if (ziel instanceof Node && tafel.contains(ziel)) return;
    const ziele = fokusZiele();
    (ziele[0] ?? tafel).focus({ preventScroll: true });
  });

  if (typeof window.matchMedia === 'function') {
    const breit = window.matchMedia(BREITE_DESKTOP);
    breit.addEventListener('change', (ereignis: MediaQueryListEvent) => {
      if (ereignis.matches) schliessen(false);
    });
  }

  // Zurück-Taste aus dem Verlauf heraus.
  window.addEventListener('pagehide', () => schliessen(false));

  // Ohne Skript lässt sich das Menü über ":target" öffnen. Steht die Adresse
  // beim Laden schon darauf, übernimmt das Skript diesen Stand.
  if (tafel.id !== '' && window.location.hash === '#' + tafel.id) oeffnen();
}

// ---------------------------------------------------------------------------
// 1b. Der angeklickte Weg im Kopf bleibt sichtbar, bis die neue Seite malt
// ---------------------------------------------------------------------------

/**
 * Wie lange die Marke stehen bleibt, wenn nichts mehr passiert.
 *
 * Kürzer als die Frist des Absendeknopfes (60 s), weil hier weniger auf dem
 * Spiel steht: Ein Seitenwechsel, der nach zehn Sekunden nicht angefangen hat,
 * kommt nicht mehr. Bliebe die Marke, sähe der Kopf dauerhaft beschäftigt aus.
 */
const WEGFRIST = 10000;

/**
 * Führt ein Verweis von hier auf eine andere Seite dieser Website?
 *
 * Nur dann lohnt die Marke. Ausgenommen sind Telefon- und Mailverweise, fremde
 * Adressen, ein neues Fenster und der Sprung innerhalb derselben Seite — in all
 * diesen Fällen wechselt die Seite nicht, und eine Wartemarke wäre eine Lüge.
 * Der Menü-Knopf trägt "#overlay" und fällt damit von selbst heraus.
 */
function fuehrtWeg(verweis: HTMLAnchorElement): boolean {
  if (verweis.target !== '' && verweis.target !== '_self') return false;
  if (verweis.hasAttribute('download')) return false;
  if (verweis.origin !== window.location.origin) return false;

  // Gleiche Seite, nur ein anderer Anker: Der Browser springt, er lädt nicht.
  return verweis.pathname !== window.location.pathname
    || verweis.search !== window.location.search;
}

/**
 * Markiert den angeklickten Weg im Seitenkopf.
 *
 * Warum es das gibt (Rafael, 17.09.2026): Zwischen dem Klick und der ersten
 * Farbe der neuen Seite vergeht Zeit — gemessen 161 ms bei fertiger Vorseite
 * und bis zu 1345 ms, wenn die vorige Seite noch ihre Bilder zieht. Der Kopf
 * stand diese Zeit unverändert da. Es gab keine Rückmeldung, dass der Klick
 * angekommen war, und das liest sich als Hänger.
 *
 * Der Griff bleibt beim Kopf. Ein Verweis mitten im Fließtext braucht das
 * nicht: Dort ist der Zeiger schon eine Hand, und wer im Text klickt, erwartet
 * ohnehin einen Sprung. Im Kopf steht die Marke für "ich bin dabei".
 *
 * Zurückgenommen wird auf zwei Wegen, denselben wie beim Absendeknopf. Erstens
 * "pageshow": Holt der Browser die Seite aus dem Vor-und-Zurück-Speicher, läuft
 * das Skript nicht neu, und die Marke des letzten Klicks stünde wieder da —
 * gemessen am 17.09.2026, dieser Speicher ist auf dieser Seite wirklich in
 * Gebrauch. Zweitens die Frist, falls der Wechsel nie beginnt.
 */
function wegmarkeEinrichten(): void {
  const kopf = eins<HTMLElement>('.kopf');
  const tafel = eins<HTMLElement>('.menue-overlay');
  const bereiche = [kopf, tafel].filter((b): b is HTMLElement => b !== null);

  if (bereiche.length === 0) return;

  let wecker = 0;

  const loeschen = (): void => {
    if (wecker !== 0) {
      clearTimeout(wecker);
      wecker = 0;
    }

    for (const markiert of alle<HTMLElement>('.unterwegs')) {
      markiert.classList.remove('unterwegs');
    }
  };

  window.addEventListener('pageshow', loeschen);

  for (const bereich of bereiche) {
    bereich.addEventListener('click', (ereignis: MouseEvent) => {
      // Ein Klick mit Zusatztaste oder mittlerer Maustaste öffnet einen neuen
      // Tab. Diese Seite bleibt dann stehen, und eine Marke bliebe hängen.
      if (ereignis.defaultPrevented) return;
      if (ereignis.button !== 0) return;
      if (ereignis.metaKey || ereignis.ctrlKey || ereignis.shiftKey || ereignis.altKey) return;

      const ziel = ereignis.target;
      if (!(ziel instanceof Element)) return;

      const verweis = ziel.closest('a[href]');
      if (!(verweis instanceof HTMLAnchorElement)) return;
      if (!fuehrtWeg(verweis)) return;

      loeschen();
      verweis.classList.add('unterwegs');
      wecker = window.setTimeout(loeschen, WEGFRIST);
    });
  }
}

// ---------------------------------------------------------------------------
// 2. Formulare, Prüfung im Browser als Ergänzung zur Prüfung auf dem Server
// ---------------------------------------------------------------------------

type Feld = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

// Bewusst so weit wie [EmailAddress] auf dem Server: genau ein @, davor und danach
// etwas ohne Leerraum. Ein Punkt in der Domain wird NICHT verlangt, sonst wiese das
// Skript Adressen ab, die der Server annehmen wuerde, und das Formular waere blockiert.
const EMAIL_MUSTER = /^[^\s@]+@[^\s@]+$/;

const NICHT_PRUEFBAR = new Set(['hidden', 'submit', 'button', 'reset', 'image', 'file']);

function istFeld(element: unknown): element is Feld {
  return element instanceof HTMLInputElement
    || element instanceof HTMLTextAreaElement
    || element instanceof HTMLSelectElement;
}

function pruefbareFelder(formular: HTMLFormElement): Feld[] {
  return Array.from(formular.elements)
    .filter(istFeld)
    .filter((feld) => !feld.disabled && !NICHT_PRUEFBAR.has(feld.type));
}

function angehakt(feld: HTMLInputElement, formular: HTMLFormElement): boolean {
  if (feld.type === 'checkbox') return feld.checked;
  const name = feld.name;
  if (name === '') return feld.checked;
  return alle<HTMLInputElement>(`input[type="radio"][name="${auswahlZeichen(name)}"]`, formular)
    .some((eins_) => eins_.checked);
}

function meldungAusZustand(feld: Feld): string {
  const zustand = feld.validity;
  if (zustand.typeMismatch) return 'Bitte prüfen Sie das Format dieser Eingabe.';
  if (zustand.patternMismatch) {
    const hinweis = feld.getAttribute('title');
    return hinweis !== null && hinweis !== '' ? hinweis : 'Bitte prüfen Sie das Format dieser Eingabe.';
  }
  if (zustand.rangeUnderflow || zustand.rangeOverflow || zustand.stepMismatch) {
    return 'Bitte prüfen Sie diesen Wert.';
  }
  return 'Bitte prüfen Sie diese Eingabe.';
}

/** Liefert den Fehlertext oder null, wenn das Feld in Ordnung ist. */
function pruefe(feld: Feld, formular: HTMLFormElement): string | null {
  const pflicht = feld.required || feld.hasAttribute('data-val-required');
  const textPflicht = feld.getAttribute('data-val-required');

  if (feld instanceof HTMLInputElement && (feld.type === 'checkbox' || feld.type === 'radio')) {
    if (pflicht && !angehakt(feld, formular)) {
      return textPflicht ?? 'Bitte bestätigen Sie diese Angabe.';
    }
    return null;
  }

  const wert = feld.value.trim();

  if (pflicht && wert === '') {
    return textPflicht ?? 'Bitte füllen Sie dieses Feld aus.';
  }
  if (wert === '') return null;

  const alsEmail = (feld instanceof HTMLInputElement && feld.type === 'email')
    || feld.hasAttribute('data-val-email');
  if (alsEmail && !EMAIL_MUSTER.test(wert)) {
    return feld.getAttribute('data-val-email') ?? 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
  }

  const hoechstens = zahlOder(feld.getAttribute('data-val-length-max') ?? feld.getAttribute('maxlength'));
  if (hoechstens !== null && wert.length > hoechstens) {
    return feld.getAttribute('data-val-length')
      ?? `Bitte geben Sie höchstens ${hoechstens} Zeichen ein.`;
  }

  const mindestens = zahlOder(feld.getAttribute('data-val-length-min') ?? feld.getAttribute('minlength'));
  if (mindestens !== null && wert.length < mindestens) {
    return feld.getAttribute('data-val-length')
      ?? `Bitte geben Sie mindestens ${mindestens} Zeichen ein.`;
  }

  if (typeof feld.checkValidity === 'function' && !feld.checkValidity()) {
    return meldungAusZustand(feld);
  }
  return null;
}

function meldungsFeld(formular: HTMLFormElement, feld: Feld): HTMLElement {
  const name = feld.name !== '' ? feld.name : feld.id;
  const schluessel = auswahlZeichen(name);

  let ziel = name !== ''
    ? eins<HTMLElement>(`[data-valmsg-for="${schluessel}"], [data-fehler-fuer="${schluessel}"]`, formular)
    : null;

  if (ziel === null) {
    ziel = document.createElement('span');
    ziel.className = 'feld-fehler';
    ziel.setAttribute('data-fehler-fuer', name);
    feld.insertAdjacentElement('afterend', ziel);
  }
  if (ziel.id === '') {
    ziel.id = `fehler-${name !== '' ? name : String(Date.now())}`;
  }
  return ziel;
}

function beschreibungSetzen(feld: Feld, id: string, dazu: boolean): void {
  const vorhanden = (feld.getAttribute('aria-describedby') ?? '').split(/\s+/).filter((t) => t !== '');
  const ohne = vorhanden.filter((t) => t !== id);
  const neu = dazu ? [...ohne, id] : ohne;
  if (neu.length === 0) feld.removeAttribute('aria-describedby');
  else feld.setAttribute('aria-describedby', neu.join(' '));
}

function fehlerZeigen(formular: HTMLFormElement, feld: Feld, text: string): void {
  const ziel = meldungsFeld(formular, feld);
  ziel.textContent = text;
  ziel.classList.add('feld-fehler', 'field-validation-error');
  ziel.classList.remove('field-validation-valid');
  feld.setAttribute('aria-invalid', 'true');
  feld.classList.add('input-validation-error');
  beschreibungSetzen(feld, ziel.id, true);
}

/** Zeigt das Feld gerade einen Fehler, gleich ob vom Server oder aus dem Browser? */
function fehlerSteht(feld: Feld): boolean {
  return feld.getAttribute('aria-invalid') === 'true'
    || feld.classList.contains('input-validation-error');
}

function fehlerLoeschen(formular: HTMLFormElement, feld: Feld): void {
  if (!fehlerSteht(feld)) return;
  const ziel = meldungsFeld(formular, feld);
  ziel.textContent = '';
  ziel.classList.remove('field-validation-error');
  ziel.classList.add('field-validation-valid');
  feld.removeAttribute('aria-invalid');
  feld.classList.remove('input-validation-error');
  beschreibungSetzen(feld, ziel.id, false);
}

/**
 * Wann eine stehengebliebene Absendesperre fällt.
 *
 * Dieselbe Frist wie im Pflegebereich und aus demselben Grund: So spät, dass eine
 * noch laufende Anfrage längst navigiert oder der Browser sie abgebrochen hätte —
 * aber nicht nie. Hier wöge das Nie sogar schwerer: Ein zweites Mal abgeschickte
 * Anfrage ist lästig, eine verlorene ist ein verlorener Gast.
 */
const ABSENDEFRIST = 60000;

function formularEinrichten(formular: HTMLFormElement): void {
  // Die eigene Meldung tritt an die Stelle der Sprechblase des Browsers.
  // Die Prüfung auf dem Server bleibt davon unberührt und entscheidet.
  formular.noValidate = true;

  let laeuft = false;
  let wecker = 0;

  /**
   * Gibt das Formular wieder frei.
   *
   * Zwei Wege führen hierher. Der eine: Holt der Browser die Seite aus dem
   * Vor-und-Zurueck-Speicher, laeuft das Skript nicht neu — ohne Zuruecksetzen
   * bliebe die Sperre stehen und jeder weitere Klick wuerde stumm verworfen.
   *
   * Der andere ist die Frist. Bricht der Gast das Absenden ab, oder reisst die
   * Verbindung mitten in der Anfrage, navigiert die Seite nie — und die Sperre
   * stand fuer immer. Gemessen am 28.08.2026: nach zwoelf Sekunden ohne
   * Navigation trug der Knopf weiterhin aria-disabled, und ein zweiter Versuch
   * wurde verworfen. Der Knopf sah beschaeftigt aus, also hatte der Gast keinen
   * Grund neu zu laden; seine Anfrage kam nie an, und niemand erfuhr davon.
   */
  const freigeben = (): void => {
    laeuft = false;

    if (wecker !== 0) {
      clearTimeout(wecker);
      wecker = 0;
    }

    for (const knopf of alle<HTMLElement>('button[type="submit"], input[type="submit"]', formular)) {
      knopf.removeAttribute('aria-disabled');
      knopf.classList.remove('laeuft');
    }
  };

  window.addEventListener('pageshow', freigeben);

  formular.addEventListener('submit', (ereignis: SubmitEvent) => {
    if (laeuft) {
      ereignis.preventDefault();
      return;
    }

    let erstesFalsch: Feld | null = null;

    for (const feld of pruefbareFelder(formular)) {
      const text = pruefe(feld, formular);
      if (text !== null) {
        fehlerZeigen(formular, feld, text);
        if (erstesFalsch === null) erstesFalsch = feld;
      } else {
        fehlerLoeschen(formular, feld);
      }
    }

    if (erstesFalsch !== null) {
      ereignis.preventDefault();
      erstesFalsch.focus();
      return;
    }

    laeuft = true;
    wecker = window.setTimeout(freigeben, ABSENDEFRIST);

    // Der Knopf wird nicht deaktiviert, sonst fehlte sein Wert im Formular.
    for (const knopf of alle<HTMLElement>('button[type="submit"], input[type="submit"]', formular)) {
      knopf.setAttribute('aria-disabled', 'true');
      knopf.classList.add('laeuft');
    }
  });

  formular.addEventListener('input', (ereignis: Event) => {
    const ziel = ereignis.target;
    if (!istFeld(ziel)) return;
    if (!fehlerSteht(ziel)) return;
    if (pruefe(ziel, formular) === null) fehlerLoeschen(formular, ziel);
  });

  formular.addEventListener('focusout', (ereignis: FocusEvent) => {
    const ziel = ereignis.target;
    if (!istFeld(ziel)) return;
    if (!fehlerSteht(ziel)) return;
    const text = pruefe(ziel, formular);
    if (text === null) fehlerLoeschen(formular, ziel);
    else fehlerZeigen(formular, ziel, text);
  });
}

function formulareEinrichten(): void {
  for (const formular of Array.from(document.forms)) {
    const hatRegel = formular.querySelector(
      '[required], [data-val-required], [data-val-email], [data-val-length], input[type="email"]',
    );
    if (hatRegel === null) continue;
    formularEinrichten(formular);
  }
}

// ---------------------------------------------------------------------------
// 2b. Der Wunschtermin der Feieranfrage: der letzte Tag folgt dem ersten
// ---------------------------------------------------------------------------

/**
 * Eine Feier dauert im Normalfall einen Tag. Wer den ersten Tag waehlt, bekommt
 * ihn als letzten vorgeschlagen, solange dort nichts steht oder das Alte vor dem
 * Neuen laege (Rafael, 07.09.2026); ein spaeteres Ende bleibt, wie der Gast es
 * gesetzt hat. Dazu rueckt die Untergrenze des zweiten Feldes auf den ersten Tag,
 * damit die Auswahl keinen frueheren anbietet. Ohne Skript bleiben beide Felder
 * frei, und der Server weist ein Ende vor dem Anfang weiterhin ab.
 */
function wunschterminEinrichten(): void {
  const von = eins<HTMLInputElement>('#anfrage #Eingabe_Von');
  const bis = eins<HTMLInputElement>('#anfrage #Eingabe_Bis');
  if (von === null || bis === null || von.type !== 'date' || bis.type !== 'date') return;

  const untergrenze = bis.min;

  const nachziehen = (): void => {
    if (von.value === '') {
      bis.min = untergrenze;
      return;
    }

    // JJJJ-MM-TT vergleicht sich als Text in der richtigen Reihenfolge.
    if (bis.value === '' || bis.value < von.value) bis.value = von.value;
    bis.min = von.value;
  };

  von.addEventListener('change', nachziehen);
  von.addEventListener('input', nachziehen);
}

// ---------------------------------------------------------------------------
// 3. Sanftes Einblenden von Bildern

// ---------------------------------------------------------------------------
// Start
// ---------------------------------------------------------------------------

/**
 * Laesst die Bilder einer Bildstelle einander abloesen, und laesst den Gast
 * selbst blaettern.
 *
 * Ohne dieses Skript bleibt das erste Bild stehen. Das ist kein Notbehelf, sondern
 * die Absicht: Eine Bildfolge ist Schmuck, und die Seite ist auch mit einem Bild
 * vollstaendig.
 *
 * Wer weniger Bewegung eingestellt hat, bekommt den Wechsel ohne Blende: site.css
 * nimmt der Folge dann den Uebergang, und die Bilder wechseln hart. Bis zum
 * 05.09.2026 blieb die Folge unter dieser Einstellung ganz stehen -- und Android
 * meldet sie schon, wenn jemand die Animationen des Systems abstellt, etwa in den
 * Entwickleroptionen. Auf Rafaels Telefon stand der Hero deshalb still, waehrend
 * er am Schreibtisch wechselte. Ein Bild, das alle sechs Sekunden ohne Bewegung
 * ausgetauscht wird, ist keine Animation; was die Einstellung abbestellt, ist die
 * Blende, und die faellt weg.
 *
 * Wischen (05.09.2026): nach links zum naechsten Bild, nach rechts zum vorigen,
 * mit derselben Schwelle wie auf der Oeffnungstafel. Die Uhr faengt danach von
 * vorn an, damit das gewaehlte Bild seine volle Zeit bekommt. Punkte im Bild
 * sagen, wo man ist; sie entstehen erst hier, denn ohne Skript gibt es nichts zu
 * wischen. Im Hero ist der ganze Abschnitt die Wischflaeche: Der Text liegt ueber
 * dem Bild und finge die Bewegung sonst ab.
 */
/**
 * Holt die Bilder eines zurueckgehaltenen Blattes nach.
 *
 * Die Blaetter hinter dem ersten reisen ohne src und srcset; ihre Adressen stehen
 * in data-Attributen (siehe Fotoangabe.Spaeter). Ohne das laedt der Browser sie
 * beim Aufbau mit, denn sie liegen deckungsgleich ueber dem sichtbaren Blatt und
 * sind fuer ihn damit in Sicht -- auf der Startseite waren das 704 KB, die
 * niemand sieht.
 *
 * Erst die Quellen, dann das img: Der Browser waehlt aus den source-Elementen aus,
 * sobald das img eine Adresse bekommt. Stuenden sie da noch nicht, naehme er das
 * JPEG des Rueckfalls und das WebP waere umsonst erzeugt.
 *
 * Zweimal gerufen schadet nicht: Beim ersten Mal wandert die Adresse aus dem
 * data-Attribut, danach findet die Suche nichts mehr.
 */
function blattLaden(blatt: HTMLElement): void {
  // Nicht [data-src], sondern [data-src] mit Inhalt: Razor laesst ein Attribut mit
  // dem Wert null weg -- ausser wenn es mit "data-" anfaengt. An jedem nicht
  // zurueckgehaltenen Bild steht deshalb data-src="" da. Wer nur nach dem Attribut
  // fragt, trifft auch das sichtbare Bild und setzt ihm src="" -- also weg damit.
  // Am 18.09.2026 gemessen, siehe _Foto.cshtml.
  for (const quelle of alle<HTMLSourceElement>('source[data-srcset]:not([data-srcset=""])', blatt)) {
    quelle.srcset = quelle.dataset['srcset'] ?? '';
    delete quelle.dataset['srcset'];
  }

  for (const bild of alle<HTMLImageElement>('img[data-src]:not([data-src=""])', blatt)) {
    // Zuerst zuruecktreten, dann laden. Ein nachgeholtes Blatt ist Schmuck: Es
    // steht nie oben auf der Seite, es ist nie das groesste Bild, und niemand
    // wartet darauf. Eine Anfrage, die der Gast selbst ausgeloest hat -- der Klick
    // auf die naechste Seite --, wiegt schwerer und soll vorbei duerfen.
    //
    // Warum das nachgereicht wurde: 938be22 hat die unsichtbaren Blaetter aus dem
    // Aufbau genommen, aber sie starten seither alle beim load-Ereignis auf einen
    // Schlag -- auf der Startseite 687 KB. Die Konkurrenz war damit verschoben und
    // nicht weg. Mit niedriger Stufe raeumt sie einer Navigation das Feld.
    //
    // Vor dem Setzen der Adresse, sonst laeuft die Anfrage schon.
    bild.fetchPriority = 'low';

    const satz = bild.dataset['srcset'];

    if (satz !== undefined && satz !== '') {
      bild.srcset = satz;
      delete bild.dataset['srcset'];
    }

    bild.src = bild.dataset['src'] ?? '';
    delete bild.dataset['src'];
  }
}

function bildfolgenEinrichten(): void {
  const folgen = alle<HTMLElement>('[data-bildfolge]');

  for (const folge of folgen) {
    const blaetter = alle<HTMLElement>('[data-bildfolge-blatt]', folge);

    if (blaetter.length < 2) {
      continue;
    }

    let jetzt = 0;
    let uhr: number | undefined;

    const sekunden = (blatt: HTMLElement): number => {
      const roh = Number.parseInt(blatt.dataset['sekunden'] ?? '', 10);

      return Number.isFinite(roh) ? Math.min(60, Math.max(2, roh)) : 6;
    };

    // Die Punkte: einer je Blatt, der gefuellte ist das gezeigte. Nur eine
    // Anzeige, kein Knopf -- in den Kacheln laege ein Knopf in einem Verweis,
    // und das ist kein gueltiges Markup. Vorleser brauchen sie nicht: Fuer sie
    // ist immer nur ein Bild da, die uebrigen Blaetter tragen aria-hidden.
    const leiste = document.createElement('div');
    leiste.className = 'bildfolge-punkte';
    leiste.setAttribute('aria-hidden', 'true');

    const punkte = blaetter.map(() => {
      const punkt = document.createElement('span');
      punkt.className = 'bildfolge-punkt';
      leiste.appendChild(punkt);

      return punkt;
    });

    folge.appendChild(leiste);

    const zeigen = (platz: number): void => {
      // Nur das Blatt, das gleich zu sehen ist -- und wirklich nur dieses. Im
      // Normalfall ist es laengst geholt; das geschieht, sobald die Seite steht.
      // Auf einer sehr langsamen Leitung kann der erste Wechsel aber vorher dran
      // sein, und dann stuende hier ein leeres Bild.
      //
      // Hier stand einmal auch das naechste Blatt, damit es Vorlauf hat. Das war
      // ein Fehler: Der erste Aufruf ist zeigen(0) beim Einrichten, also lange vor
      // "load" -- und holte damit genau das zweite Blatt vor, um dessen 559 KB es
      // in dieser Aenderung geht.
      const kommend = blaetter[platz];

      if (kommend !== undefined) {
        blattLaden(kommend);
      }

      blaetter.forEach((blatt, i) => {
        const sichtbar = i === platz;

        blatt.classList.toggle('ist-sichtbar', sichtbar);

        // Fuer Vorleseprogramme ist immer nur ein Bild da. Ohne das kuendigte die
        // Seite dieselbe Stelle so oft an, wie Bilder hinterlegt sind.
        blatt.setAttribute('aria-hidden', sichtbar ? 'false' : 'true');
      });

      punkte.forEach((punkt, i) => {
        punkt.classList.toggle('ist-aktiv', i === platz);
      });
    };

    const anhalten = (): void => {
      if (uhr !== undefined) {
        window.clearTimeout(uhr);
        uhr = undefined;
      }
    };

    const weiter = (): void => {
      anhalten();

      if (document.hidden) {
        return;
      }

      const blatt = blaetter[jetzt];

      if (blatt === undefined) {
        return;
      }

      uhr = window.setTimeout(() => {
        jetzt = (jetzt + 1) % blaetter.length;
        zeigen(jetzt);
        weiter();
      }, sekunden(blatt) * 1000);
    };

    const blaettern = (richtung: number): void => {
      jetzt = (jetzt + richtung + blaetter.length) % blaetter.length;
      zeigen(jetzt);
      weiter();
    };

    // Im Hintergrund nicht weiterlaufen: Sonst laeuft die Folge in einem Tab, den
    // niemand ansieht, und kostet nur Strom.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        anhalten();
      } else {
        weiter();
      }
    });

    // Sobald die Seite steht, kommen die uebrigen Blaetter dran. Nicht frueher:
    // Bis dahin gehoert die Leitung dem, was der Gast wirklich sieht -- und dem
    // Klick, mit dem er weitergeht. Ist die Seite schon fertig, wenn das Skript
    // hier ankommt, feuert "load" nicht mehr, deshalb die Abfrage davor.
    const uebrigeHolen = (): void => {
      for (const blatt of blaetter) {
        blattLaden(blatt);
      }
    };

    if (document.readyState === 'complete') {
      uebrigeHolen();
    } else {
      window.addEventListener('load', uebrigeHolen, { once: true });
    }

    wischenEinrichten(folge.closest<HTMLElement>('.hero') ?? folge, blaettern);
    zeigen(0);
    weiter();
  }
}

/**
 * Wischen auf einer Flaeche: nach links ruft blaettern(1), nach rechts
 * blaettern(-1). Nur eine waagerechte Bewegung zaehlt -- wer die Seite senkrecht
 * rollt und dabei ein wenig seitwaerts rutscht, soll nicht ploetzlich das
 * naechste Bild vor sich haben. Die Schwelle von 40 Punkten und das Verhaeltnis
 * 3:2 halten das auseinander, wie auf der Oeffnungstafel.
 *
 * Ein Wisch ist kein Klick: Nach dem pointerup schickt der Browser noch ein
 * click an das Element unter dem Zeiger, und in einer Kachel ist das der
 * Verweis zur Speisekarte. Der erste Klick nach einem Wisch wird deshalb
 * verschluckt; der naechste pointerdown hebt das wieder auf.
 */
function wischenEinrichten(flaeche: HTMLElement, blaettern: (richtung: number) => void): void {
  flaeche.classList.add('ist-wischflaeche');

  let anfangX = 0;
  let anfangY = 0;
  let unterwegs = false;
  let gewischt = false;

  flaeche.addEventListener('pointerdown', (ereignis) => {
    if (ereignis.pointerType === 'mouse' && ereignis.button !== 0) {
      return;
    }

    anfangX = ereignis.clientX;
    anfangY = ereignis.clientY;
    unterwegs = true;
    gewischt = false;
  });

  flaeche.addEventListener('pointerup', (ereignis) => {
    if (!unterwegs) {
      return;
    }

    unterwegs = false;

    const dx = ereignis.clientX - anfangX;
    const dy = ereignis.clientY - anfangY;

    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) {
      return;
    }

    gewischt = true;
    blaettern(dx < 0 ? 1 : -1);
  });

  flaeche.addEventListener('pointercancel', () => {
    unterwegs = false;
  });

  // Der Browser zieht Bilder und markierten Text als Objekt durch die Gegend,
  // sobald man sie mit der Maus bewegt -- und bricht dafuer die Zeigerfolge ab
  // (pointercancel statt pointerup). Ohne diese Zeile blaetterte der zweite
  // Wisch mit der Maus nicht mehr, weil der erste den Text darueber markiert
  // hatte; und in einer Kachel zoege die Maus das Foto statt zu blaettern.
  flaeche.addEventListener('dragstart', (ereignis) => {
    ereignis.preventDefault();
  });

  flaeche.addEventListener(
    'click',
    (ereignis) => {
      if (gewischt) {
        gewischt = false;
        ereignis.preventDefault();
        ereignis.stopPropagation();
      }
    },
    true,
  );
}

/**
 * Das Umschalten zwischen den Oeffnungsplaenen auf der Seite Anfahrt.
 *
 * Der Inhaber fuehrt benannte Saetze Oeffnungszeiten mit Gueltigkeitszeitraum —
 * "Sommer-Oeffnungszeiten" vom 1. Mai bis 30. September, "Winter-..." vom
 * 1. November bis 31. Maerz. Wer im September einen Ausflug fuer den Winter
 * plant, moechte die Winterzeiten schon kennen.
 *
 * Die Bedienung ist absichtlich klein: oben rechts in der Ecke je Plan ein
 * Punkt, der gefuellte ist der gezeigte. Ein Tipp auf einen anderen wechselt,
 * und auf der Tafel laesst sich wischen. Die Funktion ist eine Nebensache und
 * darf die Tafel nicht dominieren (Rafael, 03.09.2026: "sehr dezent").
 *
 * Alle Plaene stehen im Markup, sichtbar ist einer. Die Punkte liegen dort
 * verborgen und werden erst hier sichtbar — dieselbe Bauart wie beim Knopf
 * "weitere Termine" darunter: Ohne Skript sieht der Gast die Zeiten, die heute
 * gelten, und nichts sonst. Nie ist etwas Falsches zu sehen.
 *
 * Was hier NICHT angefasst wird: die Statuszeile ("Jetzt geoeffnet") und der
 * Hinweis auf besondere Termine. Beide sagen etwas ueber diesen Augenblick.
 * Mit den Winterzeiten mitgeschaltet stuende im Juli "Jetzt geoeffnet" ueber
 * einem Wochenplan, der im Juli nicht gilt.
 */
function zeitenwahlEinrichten(): void {
  const wahl = eins<HTMLElement>('[data-zeitenwahl]');

  if (wahl === null) {
    return;
  }

  const punkte = alle<HTMLButtonElement>('[data-zeitenplan]', wahl);
  const bloecke = alle<HTMLElement>('[data-zeitenblock]');
  const titel = eins<HTMLElement>('[data-zeiten-titel]');
  const flaeche = eins<HTMLElement>('[data-zeitenbloecke]');

  if (punkte.length < 2 || bloecke.length < 2) {
    return;
  }

  wahl.hidden = false;

  const zeigen = (kennung: string): void => {
    for (const block of bloecke) {
      const gemeint = block.dataset['zeitenblock'] === kennung;

      block.hidden = !gemeint;

      // Die Ueberschrift traegt den Namen des gezeigten Plans. Er steht am Block
      // und nicht im Skript — ein zweites Verzeichnis der Namen koennte von dem
      // im Markup abweichen.
      if (gemeint && titel !== null) {
        titel.textContent = block.dataset['zeitenname'] ?? titel.textContent;
      }
    }

    for (const punkt of punkte) {
      punkt.setAttribute('aria-pressed', punkt.dataset['zeitenplan'] === kennung ? 'true' : 'false');
    }
  };

  // Um einen Schritt weiter oder zurueck, am Ende geht es vorne weiter.
  const blaettern = (schritt: number): void => {
    const jetzt = bloecke.findIndex((block) => !block.hidden);
    const ziel = bloecke[(jetzt + schritt + bloecke.length) % bloecke.length];
    const kennung = ziel?.dataset['zeitenblock'];

    if (kennung !== undefined) {
      zeigen(kennung);
    }
  };

  for (const punkt of punkte) {
    punkt.addEventListener('click', () => {
      const kennung = punkt.dataset['zeitenplan'];

      if (kennung !== undefined) {
        zeigen(kennung);
      }
    });
  }

  // Wischen auf der Tafel: nach links zum naechsten Plan, nach rechts zum
  // vorigen. Nur eine waagerechte Bewegung zaehlt — wer die Seite senkrecht
  // rollt und dabei ein wenig seitwaerts rutscht, soll nicht ploetzlich den
  // Winter vor sich haben. Die Schwelle von 40 Punkten und das Verhaeltnis 3:2
  // halten das auseinander.
  if (flaeche === null) {
    return;
  }

  let anfangX = 0;
  let anfangY = 0;
  let unterwegs = false;

  flaeche.addEventListener('pointerdown', (ereignis) => {
    anfangX = ereignis.clientX;
    anfangY = ereignis.clientY;
    unterwegs = true;
  });

  flaeche.addEventListener('pointerup', (ereignis) => {
    if (!unterwegs) {
      return;
    }

    unterwegs = false;

    const dx = ereignis.clientX - anfangX;
    const dy = ereignis.clientY - anfangY;

    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) {
      return;
    }

    blaettern(dx < 0 ? 1 : -1);
  });

  flaeche.addEventListener('pointercancel', () => {
    unterwegs = false;
  });
}

/**
 * Das Fenster mit allen kommenden besonderen Zeiten.
 *
 * Die Oeffnungstafel zeigt nur den naechsten Termin — mehr als ein Satz ist
 * fuer die Flaeche zu viel. Stehen weitere an, traegt der Hinweis einen Knopf
 * ("+ 2 weitere Termine"), der im Markup verborgen liegt und erst hier sichtbar
 * wird: Ohne Skript gibt es das Fenster nicht, und die Tafel ist mit dem
 * naechsten Termin vollstaendig. Geschlossen wird ueber den eigenen Knopf,
 * Escape (macht der Browser) oder einen Tipp auf den Grauschleier.
 */
function besondereZeitenEinrichten(): void {
  const knopf = eins<HTMLButtonElement>('[data-zeiten-mehr]');
  const fenster = eins<HTMLDialogElement>('[data-zeiten-fenster]');

  if (knopf === null || fenster === null || typeof fenster.showModal !== 'function') {
    return;
  }

  knopf.hidden = false;

  knopf.addEventListener('click', () => {
    fenster.showModal();
  });

  fenster.addEventListener('click', (ereignis) => {
    // Nur ein Klick auf den Schleier trifft das dialog-Element selbst; alles
    // im Fenster trifft eines seiner Kinder.
    if (ereignis.target === fenster) {
      fenster.close();
    }
  });
}

/**
 * Das Bearbeiten-Fenster der Textbausteine, nur fuer Angemeldete.
 *
 * Jeder Text-Stift ist ein Verweis auf die Texteseite der Verwaltung — mit ihren
 * ueber vierzig Feldern fuer einen einzelnen Satz ein zu weiter Weg. Dieses
 * Skript faengt den Klick ab, holt den Baustein vom Server und oeffnet das
 * Fenster aus dem Seitenrahmen (_Textfenster). Gespeichert wird als
 * gewoehnliches Formular: Der Server schreibt, protokolliert und leitet auf
 * dieselbe Seite zurueck — die Rollstellung merkt sich dieses Skript.
 *
 * Ohne Skript (oder wenn der Abruf scheitert) bleibt der Stift, was er ist:
 * ein Verweis auf die Texteseite. Gaeste haben weder Stifte noch das Fenster.
 */
function textfensterEinrichten(): void {
  const gefundenesFenster = eins<HTMLDialogElement>('[data-textfenster]');
  if (gefundenesFenster === null || typeof gefundenesFenster.showModal !== 'function') return;

  // Wie beim Menue: Zweitnamen mit festem Typ, damit die Verengung auch in den
  // Ereignisbehandlern gilt.
  const fenster: HTMLDialogElement = gefundenesFenster;

  const gefundenerTitel = eins<HTMLElement>('[data-textfenster-titel]', fenster);
  const gefundenerOrt = eins<HTMLElement>('[data-textfenster-ort]', fenster);
  const gefundeneId = eins<HTMLInputElement>('[data-textfenster-id]', fenster);
  const gefundenesZurueck = eins<HTMLInputElement>('[data-textfenster-zurueck]', fenster);
  const gefundeneZeile = eins<HTMLInputElement>('[data-textfenster-zeile]', fenster);
  const gefundeneFlaeche = eins<HTMLTextAreaElement>('[data-textfenster-flaeche]', fenster);
  const weg = eins<HTMLAnchorElement>('[data-textfenster-weg]', fenster);
  const abbrechen = eins<HTMLButtonElement>('[data-textfenster-abbrechen]', fenster);
  const gefundenesFormular = eins<HTMLFormElement>('form', fenster);

  if (gefundenerTitel === null || gefundenerOrt === null || gefundeneId === null
    || gefundenesZurueck === null || gefundeneZeile === null
    || gefundeneFlaeche === null || gefundenesFormular === null) return;

  const titel: HTMLElement = gefundenerTitel;
  const ort: HTMLElement = gefundenerOrt;
  const feldId: HTMLInputElement = gefundeneId;
  const feldZurueck: HTMLInputElement = gefundenesZurueck;
  const zeile: HTMLInputElement = gefundeneZeile;
  const flaeche: HTMLTextAreaElement = gefundeneFlaeche;
  const formular: HTMLFormElement = gefundenesFormular;

  /** Der Stift eines Textbausteins zeigt auf die Texteseite mit Sprungziel. */
  const MUSTER = /^\/verwaltung\/texte#baustein-(.+)$/;

  interface Bausteinangaben {
    id: number;
    wert: string;
    beschriftung: string;
    ort: string;
    mehrzeilig: boolean;
    laenge: number;
  }

  async function oeffnen(schluessel: string): Promise<void> {
    const antwort = await fetch(
      '/verwaltung/texte?handler=Baustein&schluessel=' + encodeURIComponent(schluessel),
      { headers: { Accept: 'application/json' } },
    );
    if (!antwort.ok) throw new Error(String(antwort.status));

    const angaben = await antwort.json() as Bausteinangaben;

    titel.textContent = angaben.beschriftung;
    ort.textContent = angaben.ort;
    feldId.value = String(angaben.id);
    feldZurueck.value = window.location.pathname + window.location.search;
    if (weg !== null) weg.href = '/verwaltung/texte#baustein-' + schluessel;

    // Ein Feld je Baustein: die Zeile fuer Einzeiler, die Flaeche fuer Absaetze.
    // Das jeweils andere ist still (disabled), damit nur ein "wert" mitgeht.
    const aktiv = angaben.mehrzeilig ? flaeche : zeile;
    const passiv = angaben.mehrzeilig ? zeile : flaeche;

    passiv.disabled = true;
    passiv.hidden = true;
    passiv.value = '';

    aktiv.disabled = false;
    aktiv.hidden = false;
    aktiv.maxLength = angaben.laenge;
    aktiv.value = angaben.wert;

    fenster.showModal();
    aktiv.focus();
    aktiv.setSelectionRange(aktiv.value.length, aktiv.value.length);
  }

  document.addEventListener('click', (ereignis: MouseEvent) => {
    const ziel = ereignis.target;
    if (!(ziel instanceof Element)) return;

    const griff = ziel.closest<HTMLAnchorElement>('a.textgriff');
    if (griff === null) return;

    const treffer = MUSTER.exec(griff.getAttribute('href') ?? '');
    if (treffer === null || treffer[1] === undefined) return;

    ereignis.preventDefault();

    // Scheitert der Abruf, geht es den alten Weg: auf die Texteseite.
    oeffnen(decodeURIComponent(treffer[1])).catch(() => {
      window.location.href = griff.href;
    });
  });

  if (abbrechen !== null) {
    abbrechen.addEventListener('click', () => fenster.close());
  }

  fenster.addEventListener('click', (ereignis: MouseEvent) => {
    // Nur ein Klick auf den Schleier trifft das dialog-Element selbst.
    if (ereignis.target === fenster) fenster.close();
  });

  // Nach dem Speichern kommt dieselbe Seite frisch vom Server und stuende oben.
  // Die Rollstellung wird deshalb vor dem Absenden gemerkt und danach wieder
  // eingenommen -- nach jedem Griff zurueck an die Stelle, an der man war.
  formular.addEventListener('submit', () => {
    try {
      window.sessionStorage.setItem('textfenster-rolle', String(window.scrollY));
    } catch {
      // Ohne Speicher (privates Fenster) beginnt die Seite eben oben.
    }
  });
}

/** Nimmt die vor dem Speichern gemerkte Rollstellung wieder ein. */
function rollstellungZurueck(): void {
  try {
    const wert = window.sessionStorage.getItem('textfenster-rolle');
    if (wert === null) return;

    window.sessionStorage.removeItem('textfenster-rolle');

    // Nur ohne Sprungziel: Landet eine Abweisung auf der Texteseite, springt
    // dort der Anker, und diese Stellung gehoerte zu einer anderen Seite.
    if (window.location.hash === '') {
      window.scrollTo(0, Number.parseInt(wert, 10) || 0);
    }
  } catch {
    // Ohne Speicher gibt es auch nichts wieder einzunehmen.
  }
}

function start(): void {
  // Hier stand einmal eine Klasse "hat-js" auf dem html-Element, damit eine Regel
  // im Stylesheet erkennen kann, dass das Skript laeuft. Kein Stylesheet hat sie
  // je gelesen -- und sie wurde auch nicht gebraucht: Dass die Regel ":target"
  // fuer das Menue dem Skript nicht dazwischenfaehrt, sorgt das Skript selbst,
  // indem es den Klick abfaengt und die Adresse beim Schliessen zuruecksetzt.
  menueEinrichten();
  wegmarkeEinrichten();
  formulareEinrichten();
  wunschterminEinrichten();
  bildfolgenEinrichten();
  zeitenwahlEinrichten();
  besondereZeitenEinrichten();
  textfensterEinrichten();
  rollstellungZurueck();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}

export {};
